import { useToast } from '@components/index'
import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { useOnError } from '@services/utils/onError'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { BaseParams } from 'types/utils'

type BlobAction = 'download' | 'view'

export function useContractPreview() {
  const toast = useToast()
  const { t } = useTranslation('toast')
  const onError = useOnError() // Added useOnError

  async function downloadContract({
    id,
    params
  }: BaseParams<{
    action?: BlobAction
    payment_time?: string
    deposit_paid?: boolean
  }>) {
    if (!id) throw new Error('Contract ID is required')
    const res = await apiClient.get<Blob>(
      `${SNA_PREFIX}contracts/flats/preview`,
      {
        params: { ...params, flat_id: id },
        responseType: 'blob'
      }
    )

    // Check if the response is actually JSON (error) instead of PDF
    if (res.data.type === 'application/json') {
      // Parse the JSON error
      const text = await res.data.text()
      const errorData = JSON.parse(text)
      // Throw the error so onError is triggered
      throw errorData
    }

    return res.data
  }

  return useMutation({
    mutationFn: downloadContract,
    onSuccess: (pdfBlob: Blob, variables) => {
      const file = new Blob([pdfBlob], { type: 'application/pdf' })
      const fileURL = URL.createObjectURL(file)

      if (variables.params?.action === 'download') {
        const a = document.createElement('a')
        a.href = fileURL
        a.download = `flat-contract-${variables.id}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } else {
        const newTab = window.open(fileURL, '_blank')
        if (!newTab) {
          toast.error({
            message: t('popupBlocked')
          })
        }
      }

      URL.revokeObjectURL(fileURL)
    },
    onError // Updated to use useOnError
  })
}
